<template>
  <!--
    AB#21836 — the desk message itself. Reads as a message from a person, not a
    system alert: the sender's initials and name, a headline in the reader's
    own numbers, the sender's text, and, when the rule concerns specific
    orders, those orders as cards. Two buttons only. The tone band on top is
    the only colour that changes.
  -->
  <div class="dmc" :class="[`dmc--${card.tone}`, { 'dmc--inline': inline }]" :data-test="dataTest">
    <div class="dmc-band"></div>
    <div class="dmc-top">
      <AgentAvatar
        v-if="card.from.agent === 'elsa'"
        agent="elsa"
        :size="44"
        class="dmc-who"
        data-test="dm-from-avatar"
      />
      <UserAvatar
        v-else
        :name="card.from.name"
        :initials="card.from.initials"
        :src="card.from.avatar_url"
        :size="44"
        class="dmc-who"
        data-test="dm-from-avatar"
      />
      <div class="dmc-from">
        <b>{{ card.from.agent === 'elsa' ? 'From ELSA' : `Message from ${card.from.name}` }}</b>
        <span
          >Desk message · {{ card.rule.name }}<template v-if="when"> · {{ when }}</template
          ><template v-if="card.test"> · Test</template></span
        >
      </div>
    </div>
    <div class="dmc-body">
      <h2 class="dmc-head" data-test="dm-headline">{{ card.headline }}</h2>
      <p v-if="card.text" class="dmc-text" data-test="dm-text">{{ card.text }}</p>
      <div v-if="card.orders.length" class="dmc-orders">
        <button
          v-for="o in card.orders"
          :key="o.order_id"
          type="button"
          class="dmc-order"
          :data-test="`dm-order-${o.order_no ?? o.order_item_id}`"
          @click="emit('order', o)"
        >
          <b
            >{{ o.order_label ?? (o.order_no ? `#${o.order_no}` : 'Order number pending')
            }}<template v-if="o.company"> · {{ o.company }}</template></b
          >
          <span>{{ o.headline }}</span>
        </button>
      </div>
    </div>
    <div class="dmc-foot">
      <label v-if="card.can_hide_today && !inline" class="dmc-hide" data-test="dm-hide-label">
        <input v-model="hide" type="checkbox" data-test="dm-hide" /> Don't show this again today
      </label>
      <span class="dmc-sp"></span>
      <button
        type="button"
        class="dash-btn"
        data-test="dm-secondary"
        @click="emit('secondary', card.secondary.action)"
      >
        {{ card.secondary.label }}
      </button>
      <button
        type="button"
        class="dash-btn dash-btn--primary"
        data-test="dm-primary"
        @click="emit('primary', card.primary)"
      >
        {{ card.primary.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { DeskCard, DeskOrder } from '@/composables/useDeskMessages';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';

const props = withDefaults(
  defineProps<{ card: DeskCard; when?: string; inline?: boolean; dataTest?: string }>(),
  { when: '', inline: false, dataTest: 'desk-message-card' }
);
const emit = defineEmits<{
  (e: 'primary', primary: DeskCard['primary']): void;
  (e: 'secondary', action: 'later' | 'snooze'): void;
  (e: 'order', order: DeskOrder): void;
  (e: 'hide-change', hide: boolean): void;
}>();

const hide = ref(false);
watch(hide, (v) => emit('hide-change', v));
watch(
  () => props.card.instance_key,
  () => (hide.value = false)
);
</script>

<style scoped>
.dmc {
  width: min(94vw, 640px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  color: var(--ink);
}
.dmc--inline {
  width: auto;
  border-radius: 12px;
}
.dmc-band {
  height: 6px;
  background: var(--warning);
}
.dmc--urgent .dmc-band {
  background: var(--danger);
}
.dmc--info .dmc-band {
  background: var(--accent);
}
.dmc-top {
  padding: 20px 24px 0;
  display: flex;
  gap: 14px;
  align-items: center;
}
.dmc-who {
  flex: none;
}
.dmc-from b {
  display: block;
  font-size: 14px;
}
.dmc-from span {
  font-size: 12px;
  color: var(--ink-mute);
}
.dmc-body {
  padding: 16px 24px 4px;
}
.dmc-head {
  margin: 0 0 8px;
  font-size: 19px;
  line-height: 1.3;
  font-weight: 700;
}
.dmc-text {
  margin: 0 0 8px;
  font-size: 14.5px;
  line-height: 1.6;
}
.dmc-orders {
  display: flex;
  gap: 10px;
  margin: 12px 0 6px;
  flex-wrap: wrap;
}
.dmc-order {
  flex: 1 1 220px;
  text-align: left;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  background: var(--surface-2);
  color: var(--ink);
  cursor: pointer;
}
.dmc-order:hover {
  border-color: var(--accent);
}
.dmc-order b {
  display: block;
  font-size: 14px;
}
.dmc-foot {
  padding: 12px 24px 20px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.dmc-sp {
  flex: 1;
}
.dmc-hide {
  font-size: 12.5px;
  color: var(--ink-soft);
  display: flex;
  gap: 6px;
  align-items: center;
}
@media (max-width: 480px) {
  /* Phone: one button per row, full width, primary on top. */
  .dmc-foot {
    flex-direction: column;
    align-items: stretch;
  }
  .dmc-hide {
    order: 3;
    margin-top: 4px;
  }
  .dmc-foot .dash-btn {
    order: 2;
    width: 100%;
  }
  .dmc-foot .dash-btn--primary {
    order: 1;
  }
  .dmc-sp {
    display: none;
  }
}
</style>
